/**
 * The browser (PWA) disk-backup target, over the File System Access API.
 *
 * WHY THIS IS THE ONLY WAY TO WRITE OUTSIDE THE SANDBOX IN A BROWSER
 * -----------------------------------------------------------------
 * A web page has no path to a real folder. The File System Access API is the
 * supported mechanism: the user picks a directory once, we keep a handle, and we
 * write inside it. Two constraints are load-bearing:
 *
 *  1. PERMISSION IS NOT STICKY ACROSS SESSIONS. A saved handle reads as
 *     `granted` only after `queryPermission`/`requestPermission`, and the OS can
 *     revoke it at any time. So `ensurePermission` is consulted before every
 *     flush, and a denial is surfaced as PERMISSION_DENIED — never swallowed —
 *     so the status can say "backup is off because access was revoked" instead
 *     of silently writing nothing.
 *
 *  2. NOT EVERY BROWSER HAS IT. `showDirectoryPicker` is absent in Firefox and
 *     older Safari. Rather than feature-detect by user-agent (a lie that ages
 *     badly), we detect the function's presence and report `supported: false`,
 *     so the UI can say so and fall back to the manual export that works
 *     everywhere.
 *
 * HANDLE PERSISTENCE
 * ------------------
 * The chosen handle is stored in IndexedDB (not localStorage) so a reload can
 * re-open the SAME folder after one permission check. We store no path string —
 * a browser never gives us one; the handle itself carries the grant.
 */

import { DiskBackupError, type DiskBackupTarget, type BackupReadiness } from './diskBackupTarget';

/** Minimal structural types so this file does not need the DOM lib's exact shapes. */
interface WritableFile {
  write(data: string): Promise<void>;
  close(): Promise<void>;
}
interface FileHandle {
  createWritable(): Promise<WritableFile>;
  getFile(): Promise<{ text(): Promise<string> }>;
}
interface DirectoryHandle {
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FileHandle>;
  removeEntry(name: string): Promise<void>;
  values(): AsyncIterableIterator<[string, FileHandle]>;
  queryPermission?(o: { mode: string }): Promise<PermissionState>;
  requestPermission?(o: { mode: string }): Promise<PermissionState>;
}
type PermissionState = 'granted' | 'denied' | 'prompt';
interface PickerWindow {
  showDirectoryPicker?(options?: { mode?: 'read' | 'readwrite' }): Promise<DirectoryHandle>;
}

/**
 * The handle store is imported lazily and defensively: in a private window with
 * no IndexedDB the handle still works for this session, we simply cannot reopen
 * it after a reload, and that must not throw at module load.
 */
const handleStore = {
  async load(): Promise<DirectoryHandle | null> {
    try {
      const mod = await import('./diskHandleStore');
      return (await mod.loadHandle()) as DirectoryHandle | null;
    } catch {
      return null;
    }
  },
  async loadReadOnly(): Promise<DirectoryHandle | null> {
    const mod = await import('./diskHandleStore');
    return (await mod.loadHandleReadOnly()) as DirectoryHandle | null;
  },
  async save(handle: DirectoryHandle): Promise<void> {
    try {
      const mod = await import('./diskHandleStore');
      await mod.saveHandle(handle as unknown as object);
    } catch {
      // No IndexedDB. The handle still works this session.
    }
  },
};

export class BrowserDiskBackupTarget implements DiskBackupTarget {
  private handle: DirectoryHandle | null = null;
  private label = '';

  get supported(): boolean {
    const w = globalThis as unknown as PickerWindow;
    return typeof w.showDirectoryPicker === 'function';
  }

  async pickFolder(): Promise<string> {
    if (!this.supported) {
      throw new DiskBackupError('UNSUPPORTED', 'This browser cannot pick a backup folder.');
    }
    const w = globalThis as unknown as PickerWindow;
    try {
      this.handle = await (w.showDirectoryPicker as (o?: { mode?: 'read' | 'readwrite' }) => Promise<DirectoryHandle>)({
        mode: 'readwrite',
      });
      // Try to read the actual folder name from the handle. The File System
      // Access API does not officially expose the name, but Chromium populates
      // it as a hidden 'name' property. Fall back to a generic label if absent.
      this.label = (this.handle as unknown as { name?: string }).name ?? 'Selected folder';
      await handleStore.save(this.handle);
      return this.label;
    } catch (error) {
      // A user cancelling the picker is not an error worth a red status.
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new DiskBackupError('PERMISSION_DENIED', 'No folder was selected.');
      }
      throw new DiskBackupError('PERMISSION_DENIED', 'Could not access the selected folder.');
    }
  }

  async ensurePermission(): Promise<boolean> {
    const handle = this.handle ?? (await handleStore.load());
    if (!handle) return false;
    this.handle = handle;
    if (!this.label) {
      this.label = (handle as unknown as { name?: string }).name ?? '';
    }
    const state: PermissionState =
      (await handle.queryPermission?.({ mode: 'readwrite' }).catch(() => 'prompt' as PermissionState)) ?? 'prompt';
    if (state === 'granted') return true;
    const requested = await handle.requestPermission?.({ mode: 'readwrite' }).catch(() => 'denied' as PermissionState);
    if (requested !== 'granted') {
      throw new DiskBackupError(
        'PERMISSION_DENIED',
        'Access to the backup folder needs to be re-granted. Click "Reconnect folder" to resume automatic backup.',
      );
    }
    return true;
  }

  /**
   * Non-blocking readiness check for the automatic-backup scheduler.
   *
   * This is called from a timer callback, so it must NOT call
   * `requestPermission` — that requires a user gesture and fails silently from
   * a timer. Instead it only reads `queryPermission`, which is safe to call
   * without a gesture.
   *
   * Returns:
   *  - 'ready'           — handle exists and permission is granted
   *  - 'no_folder'       — no handle has been chosen yet
   *  - 'permission_lost' — handle exists but permission is not granted
   */
   async checkReady(): Promise<BackupReadiness> {
    const handle = this.handle ?? (await handleStore.load());
    if (!handle) return 'no_folder';
    this.handle = handle;
    if (!this.label) {
      this.label = (handle as unknown as { name?: string }).name ?? '';
    }
    const state: PermissionState =
      (await handle.queryPermission?.({ mode: 'readwrite' }).catch(() => 'denied' as PermissionState)) ?? 'denied';
    return state === 'granted' ? 'ready' : 'permission_lost';
  }

  /**
   * The folder handle, or null when none has been chosen yet — the normal
   * first-run state, which must READ as "no backup" rather than throw.
   * A revoked permission still throws: that is a real failure the status
   * must be able to report.
   */
  private async handleIfAvailable(): Promise<DirectoryHandle | null> {
    const ok = await this.ensurePermission();
    return ok ? this.handle : null;
  }

  private async handleIfReadable(): Promise<DirectoryHandle | null> {
    const handle = this.handle ?? (await handleStore.loadReadOnly());
    if (!handle) return null;
    this.handle = handle;
    if (!this.label) {
      this.label = (handle as unknown as { name?: string }).name ?? '';
    }
    const state =
      (await handle.queryPermission?.({ mode: 'read' }).catch(() => 'denied' as PermissionState)) ??
      'denied';
    return state === 'granted' ? handle : null;
  }

  private async requireHandle(): Promise<DirectoryHandle> {
    const handle = await this.handleIfAvailable();
    if (!handle) {
      throw new DiskBackupError('PERMISSION_DENIED', 'No backup folder is available.');
    }
    return handle;
  }

  async readFile(name: string): Promise<string | null> {
    // A device that has never chosen a folder has no manifest, so a read
    // answers null — exactly what the Electron target returns (NOT_FOUND)
    // and what the engine's status poll expects. Writes still go through
    // `requireHandle` and fail loudly: a backup that writes nowhere is not one.
    const dir = await this.handleIfAvailable();
    if (!dir) return null;
    try {
      const file = await dir.getFileHandle(name);
      return await (await file.getFile()).text();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotFoundError') return null;
      throw new DiskBackupError('IO_ERROR', `Could not read ${name} from the backup folder.`, error);
    }
  }

  async readFileReadOnly(name: string): Promise<string | null> {
    const dir = await this.handleIfReadable();
    if (!dir) {
      throw new DiskBackupError(
        'PERMISSION_DENIED',
        'Read permission for the saved backup folder is not already granted.',
      );
    }
    try {
      const file = await dir.getFileHandle(name);
      return await (await file.getFile()).text();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotFoundError') return null;
      throw new DiskBackupError('IO_ERROR', `Could not read ${name} from the backup folder.`, error);
    }
  }

  async appendFile(name: string, text: string): Promise<void> {
    const dir = await this.requireHandle();
    try {
      const file = await dir.getFileHandle(name, { create: true });
      const existing = await (await file.getFile()).text();
      const writable = await file.createWritable();
      // Write existing + new in one shot: a createWritable truncates by default,
      // so this is how an append is expressed without ever overwriting bytes.
      await writable.write(existing + text);
      await writable.close();
    } catch (error) {
      throw this.classifyWriteError(error, name);
    }
  }

  async writeFileAtomic(name: string, text: string): Promise<void> {
    const dir = await this.requireHandle();
    const tmp = `${name}.tmp`;
    try {
      const file = await dir.getFileHandle(tmp, { create: true });
      const writable = await file.createWritable();
      await writable.write(text);
      await writable.close();
      await this.replaceEntry(dir, tmp, name);
    } catch (error) {
      throw this.classifyWriteError(error, name);
    }
  }

  /**
   * A directory handle exposes no portable rename, so an atomic replace is
   * write-temp-then-swap. The temp is written and closed first, so the swap is
   * between two COMPLETE files — the same guarantee a rename gives, for our
   * single-writer use.
   */
  private async replaceEntry(dir: DirectoryHandle, tmp: string, name: string): Promise<void> {
    const text = await (await (await dir.getFileHandle(tmp)).getFile()).text();
    await dir.removeEntry(name).catch(() => {});
    const dest = await dir.getFileHandle(name, { create: true });
    const writable = await dest.createWritable();
    await writable.write(text);
    await writable.close();
    await dir.removeEntry(tmp).catch(() => {});
  }

  private classifyWriteError(error: unknown, name: string): DiskBackupError {
    if (error instanceof DOMException) {
      if (error.name === 'QuotaExceededError') {
        return new DiskBackupError('DISK_FULL', 'The backup disk is full. Free up space and try again.', error);
      }
      if (error.name === 'NotAllowedError') {
        return new DiskBackupError('PERMISSION_DENIED', 'Permission to write to the backup folder was denied.', error);
      }
      if (error.name === 'NotFoundError') {
        return new DiskBackupError('NOT_FOUND', `The backup folder no longer exists (${name}).`, error);
      }
    }
    return new DiskBackupError('IO_ERROR', `Could not write ${name} to the backup folder.`, error);
  }

  async listFiles(): Promise<readonly string[]> {
    const dir = await this.requireHandle();
    const names: string[] = [];
    for await (const [name] of dir.values()) names.push(name);
    return names.sort();
  }

  folderLabel(): string {
    return this.label || 'Selected folder';
  }
}

